/**
 * Offline Indicator Component
 * Shows connection status, pending actions count, and sync controls
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Wifi,
  WifiOff,
  Cloud,
  CloudOff,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  X,
  Download,
  Clock
} from 'lucide-react';

// Banner shown at top of page when offline
export function OfflineBanner({ darkMode }) {
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [showBanner, setShowBanner] = useState(false);
  const [recentlyReconnected, setRecentlyReconnected] = useState(false);
  
  // Listen for online/offline changes directly
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowBanner(false);
      setRecentlyReconnected(true);
      setTimeout(() => setRecentlyReconnected(false), 5000);
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      setShowBanner(true);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Set initial state based on current connection
    if (!navigator.onLine) {
      setShowBanner(true);
    }
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!showBanner && !recentlyReconnected) return null;

  return (
    <AnimatePresence>
      {(showBanner || recentlyReconnected) && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <div
            className={`px-4 py-2 flex items-center justify-between gap-3 ${
              isOnline
                ? 'bg-gradient-to-r from-emerald-500 to-green-500 text-white'
                : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
            }`}
            data-testid="offline-banner"
          >
            <div className="flex items-center gap-3">
              {isOnline ? (
                <>
                  <Wifi className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    Back online! All changes synced.
                  </span>
                </>
              ) : (
                <>
                  <WifiOff className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    You're offline. Changes will be saved locally.
                  </span>
                </>
              )}
            </div>
            <button
              onClick={() => {
                setShowBanner(false);
                setRecentlyReconnected(false);
              }}
              className="p-1 rounded-full hover:bg-white/20 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Small badge indicator for sidebar/header
export function OfflineStatusBadge({ darkMode, compact = false }) {
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (compact) {
    return (
      <div
        className={`relative flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
          isOnline
            ? darkMode
              ? 'bg-emerald-900/30 text-emerald-400'
              : 'bg-emerald-100 text-emerald-700'
            : darkMode
              ? 'bg-amber-900/30 text-amber-400'
              : 'bg-amber-100 text-amber-700'
        }`}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        data-testid="offline-status-badge"
      >
        {isOnline ? (
          <Cloud className="w-3 h-3" />
        ) : (
          <CloudOff className="w-3 h-3" />
        )}
        
        {/* Tooltip */}
        <AnimatePresence>
          {showTooltip && (
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 5 }}
              className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 rounded-lg text-xs whitespace-nowrap shadow-lg z-50 ${
                darkMode ? 'bg-gray-800 text-gray-200 border border-gray-700' : 'bg-white text-gray-800 border border-gray-200'
              }`}
            >
              {isOnline ? 'Connected' : 'Offline'}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
        darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
      } shadow-sm`}
      data-testid="offline-status-full"
    >
      <div className={`w-2 h-2 rounded-full ${
        isOnline ? 'bg-emerald-500' : 'bg-amber-500'
      }`} />
      
      <span className={`text-sm font-medium ${
        darkMode ? 'text-gray-200' : 'text-gray-700'
      }`}>
        {isOnline ? 'Connected' : 'Offline'}
      </span>
    </div>
  );
}

// Data freshness indicator (for cached data)
export function DataFreshnessIndicator({ isStale, isCached, lastUpdated, darkMode, className = '' }) {
  if (!isCached) return null;
  
  const formatTime = (timestamp) => {
    if (!timestamp) return 'unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };
  
  return (
    <div
      className={`inline-flex items-center gap-1.5 text-xs ${
        isStale
          ? darkMode ? 'text-amber-400' : 'text-amber-600'
          : darkMode ? 'text-gray-500' : 'text-gray-400'
      } ${className}`}
    >
      {isStale ? (
        <AlertTriangle className="w-3 h-3" />
      ) : (
        <Clock className="w-3 h-3" />
      )}
      <span>
        {isStale ? 'Stale data' : 'Cached'}
        {lastUpdated && ` • ${formatTime(lastUpdated)}`}
      </span>
    </div>
  );
}

// Prefetch button for manual offline data download
export function PrefetchButton({ darkMode, onComplete }) {
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handlePrefetch = async () => {
    setLoading(true);
    setResult(null);
    try {
      // Prefetch logic would go here
      setResult({ success: true, count: 0 });
      onComplete?.({ success: true });
    } catch (err) {
      setResult({ error: err.message });
    } finally {
      setLoading(false);
    }
  };

  if (!isOnline) {
    return (
      <div className={`text-sm ${
        darkMode ? 'text-gray-500' : 'text-gray-400'
      }`}>
        Connect to internet to download offline data
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <button
        onClick={handlePrefetch}
        disabled={loading}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
          loading
            ? darkMode
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            : darkMode
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-blue-600 text-white hover:bg-blue-700'
        }`}
        data-testid="prefetch-btn"
      >
        {loading ? (
          <>
            <RefreshCw className="w-4 h-4 animate-spin" />
            Downloading...
          </>
        ) : (
          <>
            <Download className="w-4 h-4" />
            Download for Offline Use
          </>
        )}
      </button>
      
      {result && (
        <div className={`text-sm ${
          result.error
            ? darkMode ? 'text-red-400' : 'text-red-600'
            : darkMode ? 'text-emerald-400' : 'text-emerald-600'
        }`}>
          {result.error
            ? `Error: ${result.error}`
            : `Downloaded ${result.count || 0} items`
          }
        </div>
      )}
    </div>
  );
}

export default {
  Banner: OfflineBanner,
  Badge: OfflineStatusBadge,
  Freshness: DataFreshnessIndicator,
  Prefetch: PrefetchButton
};
